/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import Add from '@material-ui/icons/Add';
import classnames from 'classnames';
import {globalGroupAccountHierarchySelector, globalGroupSelector} from '@ion/applications/src/store/selectors/global-group';
import {setAccountTree} from '@ion/core/src/entities/Accounts';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {
  CRUDPermissions,
  CTCEntity,
  Devices
} from '@ion/core/src/types/entities';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {ColumnChooserButton} from '@ion/dls/src/components/buttons/ColumnChooserButton';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import {GridClearFiltersButton} from '@ion/dls/src/components/buttons/GridClearFiltersButton';
import {IconButton, IconButtonLabelPlacement} from '@ion/dls/src/components/buttons/IconButton';
import {MoreActionButton} from '@ion/dls/src/components/buttons/MoreActionButton';
import {MoveGridButton} from '@ion/dls/src/components/buttons/MoveGridButton';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {RefreshGridButton} from '@ion/dls/src/components/buttons/RefreshGridButton';
import {SaveIconButton} from '@ion/dls/src/components/buttons/SaveIconButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {GridControlsBank} from '@ion/dls/src/components/grid/GridControlsBank';
import {ResponsiveMenu} from '@ion/dls/src/components/menus/ResponsiveMenu';
import {ViewSubGroupsSwitch} from '@ion/dls/src/components/switch/ViewSubGroupsSwitch';
import {ExportLimitNotification} from '@ion/dls/src/editors/ExportLimitNotification';
import {EmailExportRequestDialog} from '@ion/dls/src/export/EmailExportRequestDialog';
import {
  ViewExcludeInheritedGroupProps,
  ViewInheritableGroupProps
} from '@ion/dls/src/grid/IonGrid';
import {IonGridContainer} from '@ion/dls/src/grid/IonGridContainer';
import {useHasGridChanged} from '@ion/dls/src/grid/useHasGridChanged';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {CarbImportIcon} from '@ion/dls/src/icons/CarbImportIcon';
import {ImportIcon} from '@ion/dls/src/icons/ImportIcon';
import {PasswordIcon} from '@ion/dls/src/icons/PasswordIcon';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import getGlobalProp from '@ion/dls/src/props/GlobalPropsConfig';
import Render from '@ion/dls/src/render/Render';
import {
  dlsPageHeaderCtaAlignItems,
  dlsPageHeaderTitleLineHeight,
  dlsSubAccountVerticalPadding,
  toggleThemeStyle
} from '@ion/dls/src/theme/helpers';
import {dlsAlt, dlsColor, dlsLayout, dlsZIndex} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {FilterChips} from '@ion/dls/src/types/editors';
import {
  DLSTheme,
  ModularSearchProps,
  ViewSubGroupsPosition,
  ViewSubGroupsProps
} from '@ion/dls/src/types/types';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
import ExcludeInheritedGroup from '@ion/features/src/search/groups/ExcludeInheritedGroup';
import {ModularSearchWidget} from '@ion/features/src/search/ModularSearchWidget';
import {QueryChipData, SearchFilters} from '@ion/features/src/search/types';
import {isPreferenceAvailable} from '../../helpers';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import {ExportContext} from '../../types/redux';
import AccountEdit from '../admin/accounts/AccountEdit';
import {useAuthenticatedUser} from '../admin/users/useAuthenticatedUser';
import {checkSCIDevice} from '../devices/SCISettingsUtils';
import {ReportOperations} from '../reports/reportsUtils';

export interface StyleProps {
  searchInstance?: BaseSearchMapping;
  isGridInsideADialog?: boolean;
  // when true, adminContainer fills the full viewport height and
  // titleCtaHeader collapses to auto height with no bottom padding -
  // current theme only, since legacy already renders both this way ...
  useFullHeightContainer?: boolean;
};

type ToggleValueParams = string | number;

const toggleValue = (manual: ToggleValueParams, auto: ToggleValueParams): any =>
  ({searchInstance}: StyleProps): ToggleValueParams => searchInstance ? manual : auto
;

/**
 * Returns the shared `AdminContainer` styles required for supporting constrained
 * Grid height styling; i.e. ensuring the height of the grid does not extend beyond
 * that of the viewport.
 *
 * Generally, applications should not need to reference these styles, but rather,
 * they should use the shared `AdminContainer` component directly. However, for
 * applications which have not fully migrated to the `AdminContainer` component,
 * or for features which provide a custom grid container implementation, this
 * function is provided to facilitate reuse of the required styles.
 */
export const getConstrainedGridStyles = (theme: DLSTheme) => ({
  adminContainer : {
    display       : 'flex',
    flexDirection : 'column',
    overflow      : 'hidden',
    height        : ({isGridInsideADialog, useFullHeightContainer}: StyleProps) => useFullHeightContainer
      ? '100vh'
      : `calc(100vh - ${isGridInsideADialog ? '260px' : dlsLayout('header', 'desktop')}px)`,
    minHeight                      : ({isGridInsideADialog}: StyleProps) => isGridInsideADialog ? '397px' : 'auto',
    [theme.breakpoints.down('sm')] : {
      height : ({isGridInsideADialog}: StyleProps) => `calc(100vh - ${isGridInsideADialog ? '180px' : dlsLayout('header', 'mobile')}px) !important`,
      margin : '0px'
    }
  },
  adminGrid : {
    display                        : 'flex',
    flexDirection                  : 'column',
    overflow                       : 'hidden',
    marginTop                      : '0px',
    marginBottom                   : '8px',
    position                       : 'relative',
    flex                           : 1,
    [theme.breakpoints.down('sm')] : {
      margin : ({isGridInsideADialog}: StyleProps) =>
        isGridInsideADialog ? '60px 0 0' : toggleThemeStyle('23px 23px 40px 23px', '12px 6px')()
    }
  }
});

// defines the component styles ...
export const useStyles = makeStyles((theme: DLSTheme) => {
  const constrained = getConstrainedGridStyles(theme);
  return {
    ...constrained,
    jobstatusContainer : {
      marginTop                      : '40px',
      padding                        : '0px 100px',
      [theme.breakpoints.down('sm')] : {
        margin : '0px'
      }
    },
    globalSearchGrid : {
      marginTop                      : '20px',
      display                        : 'flex',
      flexDirection                  : 'column',
      overflow                       : 'hidden',
      height                         : `calc(72vh - ${dlsLayout('header', 'desktop')}px)`,
      [theme.breakpoints.down('sm')] : {
        margin : '0px'
      }
    },
    adminPageHeader : {
      alignItems                     : 'center',
      display                        : 'flex',
      justifyContent                 : 'space-between',
      margin                         : toggleThemeStyle('50px 0 0', '0px'),
      [theme.breakpoints.down('sm')] : {
        margin : '0px'
      }
    },
    adminTitle : {
      margin : ({titleSize}: {titleSize: PageTitleSize}) =>
        titleSize === PageTitleSize.LARGE ? toggleThemeStyle('50px 0 0', '0px')() : '0px',
      [theme.breakpoints.down('sm')] : {
        margin : '15px !important'
      }
    },
    titleCtaHeader : {
      alignItems : dlsPageHeaderCtaAlignItems,
      boxSizing  : 'border-box',
      gap        : toggleThemeStyle('0px', '16px'),
      height     : ({useFullHeightContainer}: StyleProps) => useFullHeightContainer
        ? 'auto'
        : toggleThemeStyle('auto', '183px')(),
      margin        : toggleThemeStyle(undefined, '0px'),
      padding       : toggleThemeStyle('0px', '40px 24px'),
      paddingBottom : ({useFullHeightContainer}: StyleProps) => useFullHeightContainer
        ? '0px'
        : toggleThemeStyle('0px', '40px')(),
      width                          : toggleThemeStyle('auto', '100%'),
      [theme.breakpoints.down('sm')] : {
        height : 'auto !important'
      }
    },
    titleAndToggle : {
      alignItems    : toggleThemeStyle('center', 'flex-start'),
      display       : 'flex',
      flexDirection : 'column',
      height        : toggleThemeStyle('auto', '103px'),
      padding       : toggleThemeStyle('0px', '8px 0px'),
      width         : toggleThemeStyle('auto', '158px')
    },
    titleLabel : {
      width         : 'max-content',
      color         : toggleThemeStyle('inherit', dlsColor('grey2')),
      fontFamily    : toggleThemeStyle('inherit', 'Roboto !important'),
      fontSize      : toggleThemeStyle('inherit', '32px !important'),
      fontStyle     : 'normal',
      fontWeight    : toggleThemeStyle('inherit', '400'),
      letterSpacing : toggleThemeStyle('inherit', '1px !important'),
      lineHeight    : dlsPageHeaderTitleLineHeight,
      margin        : 0
    },
    adminLabel : {
      display : toggleThemeStyle('block', 'none')
    },
    pageWidgets : {
      marginTop                      : '24px',
      alignItems                     : 'baseline',
      height                         : toggleValue('auto', 80),
      [theme.breakpoints.down('sm')] : {
        marginTop : '0px !important',
        height    : '0px !important'
      }
    },
    alignRight : {
      justifyContent : 'flex-end'
    },
    adminSearch : {
      maxWidth                       : '300px',
      width                          : '100%',
      [theme.breakpoints.down('sm')] : {
        maxWidth : 'none'
      }
    },
    adminCtas : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    titleCtas : {
      alignItems     : toggleThemeStyle('center', 'center'),
      display        : 'flex',
      gap            : toggleThemeStyle('0px', '16px'),
      height         : toggleThemeStyle('auto', '60px'),
      justifyContent : toggleThemeStyle('center', 'flex-end'),
      marginTop      : toggleThemeStyle('0px', '12px'),
      padding        : toggleThemeStyle('0px', '8px 0px 8px 8px')
    },
    mobileAddButton : {
      position : 'fixed',
      bottom   : '16px',
      right    : '18px',
      zIndex   : dlsZIndex('mobileAddButton')
    },
    iconButtonIcon : {
      color : dlsColor('white')
    },
    menuPaper : {
      marginTop : '35px'
    },
    groupName : {
      color : dlsColor('black'),
      ...dlsAlt('typography', 'cta2')
    },
    subAccount : {
      ...dlsSubAccountVerticalPadding,
      paddingLeft : toggleThemeStyle('35px', '0px'),
      width       : toggleThemeStyle('auto', '100%')
    },
    onlyThisAccount : {
      marginRight : 'auto'
    }
  };
});

// defines the names of each admin page which resolves to their respective
// navigation translation entries ...
export enum PageTitle {
  adminApplications = 'adminApplications',
  adminDevices = 'adminDevices',
  adminAssets = 'adminAssets',
  adminPegBehaviors = 'adminPegBehaviors',
  adminPegLabels = 'adminPegLabels',
  adminAempConfig = 'adminAempconfig',
  devices = 'devices',
  adminAccounts = 'adminAccounts',
  adminGroups = 'adminGroups',
  adminUsers = 'adminUsers',
  adminRoles = 'adminRoles',
  adminNotifications = 'adminNotifications',
  jobStatus = 'jobStatus',
  crashboxx = 'crashboxx',
  tripList = 'tripList',
  adminDeviceConfigs = 'adminDeviceConfigs',
  eventsNotifications = 'eventsNotifications'
};

// defines export limit size if selected list is exceed 3k
const EXPORT_LIMIT = 3000;

// defines page title sizes in terms of header values (h1, h2)
export enum PageTitleSize {
  LARGE = 'h1',
  SMALL = 'h2'
};

export interface CapExportOperations {
  disableExport?: boolean;
  showBulkDialog?: boolean;
  showNotificationDialog?: boolean;
  hasNoRowSelection?: boolean;
};

export interface GridOperations {
  isSortByAlphaNumeric?: boolean;
}

// defines the admin container props ...
export type AdminContainerProps = ReportOperations &
ViewSubGroupsProps &
GridOperations &
CapExportOperations &
ModularSearchProps &
ViewInheritableGroupProps &
ViewExcludeInheritedGroupProps &
{
  title: PageTitle,
  titleSize?: PageTitleSize,
  reducer: string,
  searchLabel?: string;
  search?: SearchFilters;
  onAdd?: MouseEventHandler,
  onImport?: MouseEventHandler,
  onCarbImport?: MouseEventHandler,
  hasCarbAssetImport?: boolean,
  onExport?: (params?: DownloadTemplate) => void,
  onRefresh: (refresh: boolean) => void,
  onShowColumnChooser: (show: boolean) => void,
  onMove?: () => void;
  omitAdminCTAs?: boolean;
  includeExport?: boolean,
  exportContext?: ExportContext,
  grid: any,
  onUpdateGridColumns?: Function
  includeGlobalSearch?: boolean;
  editGroup?: Function;
  isLoggedinUserAccount?: boolean;
  includeCurrentAccountEdit?: boolean;
  showMove?: boolean;
  showAdd?: boolean;
  isItemSelected?: boolean;
  isCtcAdmin?: boolean;
  showAdminLabel?: boolean;
  disableImport?: boolean;
  showFullWidthSearch?: boolean;
  searchInstance?: BaseSearchMapping;
  getSearchParams?: Function;
  getIdentifierParams?: (param: string) => void;
  onDevicePasswordImport?: () => any;
  areFiltersApplied?: boolean;
  onClearFilters?: () => void;
  getFilterQueries?: (filters: FilterChips) => void
  shouldCtaAlignRight?: boolean;
  onSCIUpdateClick?: () => void;
  selectedDevices?: Devices;
  totalRowsCount?: number;
  selectedItemsCount?: number;
  accountsPermissions?: CRUDPermissions;
  devicePasswordPermissions?: CRUDPermissions;
  importPermissions?: CRUDPermissions;
  hasExportLimit?: boolean;
  getExportColumnDefinitions?: Function;
  includeModularSearch?: boolean;
  externalQueries?: QueryChipData[];
  onQueriesChange?: (queries: QueryChipData[]) => void;
  excludedRows?: CTCEntity[];
  hasInheritableGroups?: boolean;
  includeInheritedToggle?: boolean;
  includeInheritableGroup?: boolean;
  isGridInsideADialog?: boolean;
  hasGridChanged?: boolean;
  isVbuFwEnabled?: boolean;
  containerClassName?: string;
  headerClassName?: string;
  // when true, adminContainer fills the full viewport height and
  // titleCtaHeader collapses to auto height with no bottom padding -
  // current theme only, since legacy already renders both this way ...
  useFullHeightContainer?: boolean;
};

const enabledImports = {
  [PageTitle.adminUsers]         : true,
  [PageTitle.devices]            : true,
  [PageTitle.adminAssets]        : true,
  [PageTitle.adminNotifications] : false
};

/**
 * The AdminContainer provides the unified Admin Page wrapper which allows for a
 * consistent layout of page elements and features across iOn Admin Pages.
 *
 * ```typescript
 *
 *   return (
 *     <AdminContainer grid={<AdminAssetsGrid />}
 *       reducer={'adminAssets'}
 *       title={translate('admin-assets:assets')}
 *       searchLabel={translate('admin-assets:searchAssets')}
 *       onShowColumnChooser={onShowColumnChooser}
 *       onImport={onImport}
 *       onExport={onExport}
 *       onAdd={onAdd}
 *       onUpdateGridColumns={onUpdateGridColumns} />
 *     );
 *
 * ```
 */
export const AdminContainer = ({
  grid,
  title,
  preference,
  onAdd,
  onImport,
  onExport,
  onRefresh,
  onCarbImport,
  onShowColumnChooser,
  onMove,
  getFilterQueries,
  onUpdateGridColumns,
  onDevicePasswordImport,
  omitAdminCTAs,
  includeExport,
  isLoggedinUserAccount,
  editGroup,
  exportContext,
  reducer,
  excludedRows,
  searchLabel,
  areFiltersApplied,
  onClearFilters,
  viewSubGroups,
  search,
  toggleViewSubGroups,
  toggleViewInheritableGroup,
  toggleViewExcludeInheritedGroup,
  getSearchParams,
  getIdentifierParams,
  accountsPermissions,
  devicePasswordPermissions,
  importPermissions,
  getExportColumnDefinitions,
  onSCIUpdateClick,
  hasGridChanged,
  hasCarbAssetImport = false,
  hasNoRowSelection = false,
  totalRowsCount = 0,
  selectedItemsCount = 0,
  selectedDevices = [],
  titleSize = PageTitleSize.LARGE,
  searchInstance = null,
  viewInheritableGroup = false,
  viewExcludeInheritedGroup = false,
  hasInheritableGroups = false,
  includeInheritedToggle = false,
  autoSelectNext = false,
  externalQueries,
  onQueriesChange,
  showMove = false,
  showAdd = true,
  isItemSelected = false,
  isCtcAdmin = false,
  showAdminLabel = true,
  showFullWidthSearch = false,
  includeGlobalSearch = false,
  includeCurrentAccountEdit = false,
  disableImport = false,
  includeInheritableGroup = false,
  hasAddPermission = true,
  shouldCtaAlignRight = false,
  hideSubGroups = false,
  hasExportLimit = false,
  disableExport = false,
  showBulkDialog = false,
  isVbuFwEnabled = false,
  showNotificationDialog = false,
  includeModularSearch = true,
  isGridInsideADialog = false,
  containerClassName,
  headerClassName,
  useFullHeightContainer = false
}: AdminContainerProps) => {
  const dispatch                                    = useDispatch();
  const classes                                     = useStyles({
    searchInstance,
    titleSize,
    isGridInsideADialog,
    useFullHeightContainer
  });
  const {isDesktop, isPhone}                        = useMedia();
  const {isCurrent: isCurrentTheme}                 = useThemeContext();
  const actionMenuRef                               = useRef(null);
  const {pending}                                   = useSelector(({exporter}) => exporter.current);
  const reportPage                                  = useSelector(({nav}) => nav?.currentPage);
  const isExporting                                 = pending[exportContext] === true;
  const importPending                               = useSelector(({importer}) => importer.current);
  const isImporting                                 = importPending?.pending?.[exportContext];
  const enableImports                               = enabledImports[title] === true && !disableImport;
  const user                                        = useAuthenticatedUser();
  const [autoSaveGridOption, setAutoSaveGridOption] = useState(false);
  const [gridSaveInProgress, setGridSaveInProgress] = useState(false);
  const [actionMenuOpen, setActionMenuOpen]         = useState(false);
  const [showPromptDialog, setShowPromptDialog]     = useState(false);
  const [isEmailExportRequest,
    setIsEmailExportRequest]                        = useState<boolean>(false);
  const [emailExportSnackbarMessage,
    setEmailExportSnackbarMessage]                  = useState<string>('');
  const reduxHasChangeInGrid                        = useHasGridChanged(reducer);
  const hasChangeInGrid                             = hasGridChanged ?? reduxHasChangeInGrid;
  const gridMode                                    = hasChangeInGrid ? 'changed' : 'default';
  const gridState                                   = gridSaveInProgress ? 'inprogress' : gridMode;
  const globalGroup                                 = useSelector(globalGroupSelector);
  const accountHierarchy                            = useSelector(globalGroupAccountHierarchySelector);
  const currentAccountId                            = parseInt(globalGroup?.href.split('/').reverse()[0], 10);
  const enableExport                                = disableExport ? selectedItemsCount === 0 : false;
  const gridExportCount                             = grid?.props?.areTotalRowsSelected
    ? totalRowsCount : selectedItemsCount || totalRowsCount;
  const isAboveExportLimit                          = gridExportCount > EXPORT_LIMIT;
  const showImportButton                            = enableImports &&
    (hasAddPermission || importPermissions?.hasUpdatePermission);
  const importButtonLabel                           = translate(`common:editors.import.button.${hasAddPermission ? 'label' : 'updateLabel'}`);

  const accountTree = useMemo(() => (
    setAccountTree(accountHierarchy, true)
  ), [accountHierarchy]);

  const isRootAccount = useMemo(() => (
    (isCtcAdmin && title === PageTitle.adminRoles) && currentAccountId === Number(get('rootAccountId'))
  ), [title, currentAccountId, isCtcAdmin]);

  useEffect(() => {
    setAutoSaveGridOption(isPreferenceAvailable('persistGridLayouts'));
  }, [user]);

  const toggleSubGroupsView = useCallback(() => {
    toggleViewSubGroups?.(!viewSubGroups);
  }, [viewSubGroups]);

  const toggleInheritableGroupView = useCallback(() => {
    toggleViewInheritableGroup?.(!viewInheritableGroup);
  }, [viewInheritableGroup]);

  const toggleExcludeInheritedGroupView = useCallback(() => {
    toggleViewExcludeInheritedGroup?.(!viewExcludeInheritedGroup);
  }, [viewExcludeInheritedGroup]);

  /**
   * @private
   */
  const updateGridSettings = function() {
    if (hasChangeInGrid && onUpdateGridColumns) {
      setGridSaveInProgress(true);
      onUpdateGridColumns();
      // adding delay help to show the transition of save icon
      // from changed to default.
      setTimeout(() => {
        setGridSaveInProgress(false);
        dispatch(toggleUserPrompt({
          message          : translate('common:grids.success'),
          autoHideDuration : 3000
        }));
      }, 1000);
    }
  };

  const devicePasswordClick = useCallback(() => {
    onDevicePasswordImport();
    setActionMenuOpen(false);
  }, [onDevicePasswordImport]);

  const onToggleActionMenu = useCallback(() => {
    setActionMenuOpen(!actionMenuOpen);
  }, [actionMenuOpen]);

  const sciUpdateClick = useCallback(() => {
    onSCIUpdateClick();
    setActionMenuOpen(false);
  }, [onSCIUpdateClick]);

  const sciDisabled = useMemo(() => (
    selectedDevices?.length ? !selectedDevices?.some(checkSCIDevice) : false
  ), [selectedDevices]);

  const onExportCSV = useCallback(() => {
    const isValidationEnabled = getGlobalProp('grids.export.validation.enabled', false) as boolean;
    if (hasExportLimit) {
      if (isAboveExportLimit && showBulkDialog) {
        setIsEmailExportRequest(true);
      } else if (isAboveExportLimit && showNotificationDialog) {
        setShowPromptDialog(true);
      } else if (gridExportCount || isValidationEnabled) {
        onExport();
      }
    } else {
      onExport();
    }
  }, [isAboveExportLimit, gridExportCount, hasExportLimit, showBulkDialog, showNotificationDialog]);

  const onCloseEmailExportDialog = useCallback(() => {
    setIsEmailExportRequest(false);
  }, []);

  const handleSnackBar = useCallback(() => {
    setEmailExportSnackbarMessage('');
  }, []);

  return (
    <IonGridContainer>
      <div className={classnames(
        includeGlobalSearch ? classes.globalSearchGrid : (reportPage?.indexOf('reports') !== -1)
          ? classes.jobstatusContainer : classes.adminContainer, containerClassName
      )}>
        <Render.If condition={!includeGlobalSearch}>
          <>
            <div className={classnames(
              classes.adminPageHeader, classes.adminTitle, classes.titleCtaHeader, headerClassName
            )}>
              <Render.If condition={isDesktop}>
                <>
                  <Render>
                    <Render.If condition={isCurrentTheme}>
                      <div className={classes.titleAndToggle}>
                        <Render.If condition={!isCtcAdmin && showAdminLabel}>
                          <HeaderTypography className={classes.adminLabel} variant='h4'>
                            {translate('nav:side.admin')}
                          </HeaderTypography>
                        </Render.If>
                        <HeaderTypography className={classes.titleLabel} variant={titleSize}>
                          {translate(`nav:side.${title}`)}
                        </HeaderTypography>
                        <Render.If condition={!hideSubGroups && !!toggleViewSubGroups}>
                          <div className={classes.subAccount}
                            style={{marginTop: toggleThemeStyle(!isCtcAdmin && showAdminLabel ? '24px' : '0', '0')()}}>
                            <ViewSubGroupsSwitch checked={viewSubGroups}
                              onChange={toggleSubGroupsView}
                              onChecked={toggleViewSubGroups}
                              preference={preference}
                              position={ViewSubGroupsPosition.GRID_CTAS}
                              labelColor={dlsColor('grey2')} />
                          </div>
                        </Render.If>
                      </div>
                    </Render.If>
                    <Render.Else>
                      <>
                        <div>
                          <Render.If condition={!isCtcAdmin && showAdminLabel}>
                            <HeaderTypography variant='h4'>
                              {translate('nav:side.admin')}
                            </HeaderTypography>
                          </Render.If>
                          <HeaderTypography variant={titleSize}>
                            {translate(`nav:side.${title}`)}
                          </HeaderTypography>
                        </div>
                        <Render.If condition={!hideSubGroups && !!toggleViewSubGroups}>
                          <div className={classes.subAccount} style={{marginTop: !isCtcAdmin && showAdminLabel ? '24px' : '0'}}>
                            <ViewSubGroupsSwitch checked={viewSubGroups}
                              onChange={toggleSubGroupsView}
                              onChecked={toggleViewSubGroups}
                              preference={preference}
                              position={ViewSubGroupsPosition.GRID_CTAS}
                              labelColor={dlsColor('grey2')} />
                          </div>
                        </Render.If>
                      </>
                    </Render.Else>
                  </Render>
                  <div className={classes.onlyThisAccount}>
                    <Render.If condition={includeInheritedToggle}>
                      <div>
                        <ExcludeInheritedGroup checked={viewExcludeInheritedGroup}
                          onChange={toggleExcludeInheritedGroupView}
                          position={ViewSubGroupsPosition.GRID_CTAS} />
                      </div>
                    </Render.If>
                  </div>
                  <Render.If condition={includeCurrentAccountEdit}>
                    <AccountEdit editGroup={editGroup}
                      isLoggedinUserAccount={isLoggedinUserAccount}
                      accountsPermissions={accountsPermissions} />
                  </Render.If>
                  <div className={classes.titleCtas}>
                    <Render.If condition={enableImports && hasAddPermission}>
                      <TertiaryButton usecaactive
                        disabled={isImporting}
                        onClick={onImport}
                        startIcon={
                          <ImportIcon color={isImporting
                            ? dlsAlt('components', 'btn', 'disabledColor') : dlsColor('aaBlue')} />
                        }
                        noUnderline>
                        <span>{importButtonLabel}</span>
                      </TertiaryButton>
                    </Render.If>
                    <Render.If condition={hasCarbAssetImport}>
                      <TertiaryButton usecaactive
                        disabled={isImporting}
                        onClick={onCarbImport}
                        startIcon={
                          <CarbImportIcon color={isImporting
                            ? dlsAlt('components', 'btn', 'disabledColor') : dlsColor('aaBlue')} />
                        }
                        noUnderline>
                        <span>{translate('common:editors.import.title', {entity: 'CARB Data'})}</span>
                      </TertiaryButton>
                    </Render.If>
                    <div>
                      <Render.If condition={showAdd && hasAddPermission}>
                        <PrimaryButton startIcon={<Add />} onClick={onAdd} disabled={isRootAccount}>
                          <span>{translate('common:addNew')}</span>
                        </PrimaryButton>
                      </Render.If>
                    </div>
                  </div>
                </>
              </Render.If>
              <Render.If condition={isPhone && showAdd && hasAddPermission}>
                <div className={classes.mobileAddButton}>
                  <IconButton icon={<Add className={classes.iconButtonIcon} />}
                    onClick={onAdd}
                    backgroundColor={dlsColor('grey1')} disabled={isRootAccount}/>
                </div>
              </Render.If>
            </div>
            <div className={classnames(classes.adminPageHeader, {
              [classes.pageWidgets] : !isCurrentTheme,
              [classes.alignRight]  : shouldCtaAlignRight
            })}>
              <Render.If condition={includeModularSearch && searchInstance}>
                <ModularSearchWidget searchInstance={searchInstance}
                  isPhone={isPhone}
                  isMobileModularSearch={isCtcAdmin}
                  isGridInsideADialog={isGridInsideADialog}
                  getFilterQueries={getFilterQueries}
                  inputPlaceholder={searchLabel ?? translate('common:modularsearch.inputplaceholder', {pagename: title})}
                  isCaseInsensitive={isCtcAdmin}
                  hasDateField={isCtcAdmin}
                  viewInheritableGroup={viewInheritableGroup}
                  toggleInheritableGroupView={toggleInheritableGroupView}
                  getSearchParams={getSearchParams}
                  getIdentifierParams={getIdentifierParams}
                  hasInheritableGroups={hasInheritableGroups}
                  pageName={title}
                  autoSelectNext={autoSelectNext}
                  showFullWidthSearch={showFullWidthSearch}
                  accountTree={accountTree}
                  accountId={currentAccountId}
                  viewSubGroups={viewSubGroups}
                  externalQueries={externalQueries}
                  onQueriesChange={onQueriesChange} />
              </Render.If>
              <Render.If condition={!omitAdminCTAs}>
                <>
                  <Render.If condition={isDesktop}>
                    <GridControlsBank controlsClassName={classes.adminCtas}>
                      <GridClearFiltersButton onClearFilters={onClearFilters}
                        areFiltersApplied={areFiltersApplied}
                        currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                      <Render.If condition={isCtcAdmin || !!onRefresh}>
                        <RefreshGridButton onRefresh={onRefresh}
                          currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                      </Render.If>
                      <Render.If condition={!!onUpdateGridColumns && !autoSaveGridOption}>
                        <SaveIconButton type={gridState}
                          currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL}
                          onClick={updateGridSettings} />
                      </Render.If>
                      <Render.If condition={includeExport}>
                        <CSVExportButton onExport={onExportCSV}
                          isExporting={isExporting}
                          entity={exportContext}
                          exportLabel={translate('common:export.csvLabel')}
                          enableExport={enableExport}
                          exportLimit={isAboveExportLimit}
                          hasNoRowSelection={hasNoRowSelection}
                          disableExport={disableExport}
                          currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                      </Render.If>
                      <Render.If condition={showMove && !!onMove && isCtcAdmin}>
                        <MoveGridButton onMove={onMove} disabled={!isItemSelected} />
                      </Render.If>
                      <ColumnChooserButton onShowColumnChooser={onShowColumnChooser}
                        currentLabelPlacement={IconButtonLabelPlacement.HORIZONTAL} />
                      <Render.If condition={isCtcAdmin && title === PageTitle.devices}>
                        <div ref={actionMenuRef}>
                          <MoreActionButton onAction={onToggleActionMenu} />
                        </div>
                      </Render.If>
                    </GridControlsBank>
                  </Render.If>
                  <ResponsiveMenu open={actionMenuOpen}
                    onClose={onToggleActionMenu}
                    anchorEl={actionMenuRef.current}
                    menuClasses={{paper: classes.menuPaper}}>
                    <CellMenuItem icon={<PasswordIcon color={dlsColor('grey3')} />}
                      disabled={!devicePasswordPermissions?.hasAddPermission}
                      label={translate('grids:selectionsHeader.devicePassword')}
                      onClick={devicePasswordClick} />
                    <Render.If condition={!!onSCIUpdateClick}>
                      <CellMenuItem icon={<PasswordIcon color={dlsColor('grey3')} />}
                        label={translate('admin-devices:view.bulkSciSetting.sciUpdate')}
                        onClick={sciUpdateClick} />
                    </Render.If>
                  </ResponsiveMenu>
                </>
              </Render.If>
            </div>
          </>
        </Render.If>
        <div className={classes.adminGrid}>
          {grid}
        </div>
        <Render.If condition={showPromptDialog}>
          <ExportLimitNotification show={showPromptDialog}
            exportCountLimit={EXPORT_LIMIT}
            showDialog={setShowPromptDialog}
            onExport={onExport} />
        </Render.If>
        <Render.If condition={isEmailExportRequest}>
          <EmailExportRequestDialog exportCount={gridExportCount}
            show={isEmailExportRequest}
            excludedRows={excludedRows}
            search={search}
            exportContext={exportContext}
            viewSubGroups={viewSubGroups}
            accountId={currentAccountId}
            isVbuFwEnabled={isVbuFwEnabled}
            includeInheritableGroup={includeInheritableGroup}
            hasInheritableGroups={hasInheritableGroups}
            searchFilters={grid?.props?.searchFilters}
            getExportColumnDefinitions={getExportColumnDefinitions}
            showSnackbar={setEmailExportSnackbarMessage}
            onClose={onCloseEmailExportDialog} />
        </Render.If>
        <Render.If condition={!!emailExportSnackbarMessage}>
          <IonSnackbar show={!!emailExportSnackbarMessage}
            message={emailExportSnackbarMessage}
            actionLabel={translate('common:editors.delete.ok')}
            onAction={handleSnackBar}
            onClose={handleSnackBar}
            autoHideDuration={3000} />
        </Render.If>
      </div>
    </IonGridContainer>
  );
};

export default AdminContainer;
